/**
 * Canonical DOM predicates for an element's "editability".
 *
 * PATTERN TO FOLLOW: the question "is this element editable?" is answered ONLY
 * from here. This logic (`TAGS.has(tagName) || isContentEditable`) used to
 * be copied into `lib/gestures/guards`, `lib/interaction/context`,
 * `components/ui/dialog` and `todo-detail.helpers` with four slightly
 * different variants, risking divergence. Import these helpers instead of rewriting
 * the check.
 *
 * There are two predicates with DIFFERENT semantics — pick based on intent:
 *  - `isEditableElement`: any form control (includes SELECT,
 *    checkbox…) or `contenteditable`. Use it to *suppress* global
 *    gestures/shortcuts when focus is inside a control.
 *  - `isTextEntryElement`: ONLY free text entry (textarea or a textual-type
 *    input). Stricter. Use it for decisions based on the text
 *    cursor, e.g. not stealing arrow keys while editing text.
 *
 * Note: there are one-off checks (e.g. in the detail's key handling) that
 * use their own tag sets on purpose (excluding SELECT, including BUTTON/A…)
 * because they're asking something different ("does the control consume this key?"). Those
 * must NOT be collapsed here: the different semantics is intentional.
 */

/** Form controls that count as "editable". */
const FORM_CONTROL_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** `<input>` types that accept free text entry with a cursor. */
const TEXT_INPUT_TYPES = new Set(["text", "search", "url", "tel", "email", "password", "number"]);

/**
 * `true` if `el` is a form control (input/textarea/select) or a
 * `contenteditable` element.
 */
export function isEditableElement(el: Element | null): el is HTMLElement {
  return el instanceof HTMLElement && (FORM_CONTROL_TAGS.has(el.tagName) || el.isContentEditable);
}

/**
 * `true` if `el` accepts free text entry: a `<textarea>` or a textual-type
 * `<input>`. Stricter than {@link isEditableElement} (excludes SELECT,
 * checkbox, radio, etc.).
 */
export function isTextEntryElement(
  el: Element | null,
): el is HTMLTextAreaElement | HTMLInputElement {
  if (el instanceof HTMLTextAreaElement) {
    return true;
  }
  if (el instanceof HTMLInputElement) {
    return TEXT_INPUT_TYPES.has(el.type.toLowerCase());
  }
  return false;
}

/**
 * `true` if any editable field has focus, or any
 * `contenteditable` exists in the document. Meant for global gates (dialog
 * dragging, navigation swipe) that must yield to editing. SSR-safe.
 */
export function isEditingActive(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  if (document.querySelector('[contenteditable="true"], [contenteditable=""]') !== null) {
    return true;
  }
  return isEditableElement(document.activeElement);
}
